import { getAuth } from 'firebase/auth'
import { ApiError } from '@/util/errors/api-error'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kPaymentsDetailsPath } from '@/constants/constants-api-paths'
import { CustomerPaymentDetailsResponseSchema, CustomerPaymentDetailsResponse } from '@/account/store/schemas'

/**
 * Gets payment details for the current user
 *
 * @returns CustomerPaymentDetailsResponse
 */
export async function getPaymentDetails(): Promise<CustomerPaymentDetailsResponse> {
  const sentryTransactionId = Date.now().toString()
  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error('Could not retrieve auth token.')

    const requestOptions: RequestInit = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kPaymentsDetailsPath}`
    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (!response.ok) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
          extra: {
            requestUrl: apiUrl,
            requestOptions,
            response: JSON.stringify(response),
            data: JSON.stringify(data),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiError(response.status, `Fetch error - status: ${response.status}, detail: ${JSON.stringify(data.detail)}`)
    }

    // Validate and return
    return CustomerPaymentDetailsResponseSchema.parse(data)
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error)
      })
    }
    throw error
  }
}
